// redux/slices/plaidSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createLinkToken as createLinkTokenAPI,
  exchangePublicToken as exchangePublicTokenAPI,
  getConnectedAccounts as getConnectedAccountsAPI,
  syncAccountTransactions as syncAccountTransactionsAPI,
  disconnectBankAccount as disconnectBankAccountAPI,
  refreshAllAccounts as refreshAllAccountsAPI,
} from "../../services/plaidService";

// Async thunk for creating link token
export const createLinkToken = createAsyncThunk(
  "plaid/createLinkToken",
  async (_, { rejectWithValue }) => {
    try {
      const response = await createLinkTokenAPI();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for exchanging public token
export const exchangePublicToken = createAsyncThunk(
  "plaid/exchangePublicToken",
  async ({ publicToken, metadata }, { rejectWithValue }) => {
    try {
      const response = await exchangePublicTokenAPI(publicToken, metadata);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching connected accounts
export const fetchConnectedAccounts = createAsyncThunk(
  "plaid/fetchConnectedAccounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getConnectedAccountsAPI();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for syncing account transactions
export const syncAccountTransactions = createAsyncThunk(
  "plaid/syncAccountTransactions",
  async ({ accountId, options }, { rejectWithValue }) => {
    try {
      const response = await syncAccountTransactionsAPI(accountId, options);
      return { accountId, ...response };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for disconnecting bank account
export const disconnectBankAccount = createAsyncThunk(
  "plaid/disconnectBankAccount",
  async (accountId, { rejectWithValue }) => {
    try {
      const response = await disconnectBankAccountAPI(accountId);
      return { accountId, ...response };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for refreshing all accounts
export const refreshAllAccounts = createAsyncThunk(
  "plaid/refreshAllAccounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await refreshAllAccountsAPI();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  // Link token for Plaid Link
  linkToken: null,
  linkTokenExpiration: null,

  // Connected accounts
  connectedAccounts: [],

  // Loading states
  loading: false,
  linkTokenLoading: false,
  accountsLoading: false,
  syncingAccounts: {},
  disconnectingAccounts: {},

  // Error states
  error: null,
  linkTokenError: null,

  // Last sync information
  lastSyncTime: null,

  // Connection status
  isInitialized: false,
};

const plaidSlice = createSlice({
  name: "plaid",
  initialState,
  reducers: {
    // Clear errors
    clearError: (state) => {
      state.error = null;
      state.linkTokenError = null;
    },

    // Clear link token (when connection is complete or cancelled)
    clearLinkToken: (state) => {
      state.linkToken = null;
      state.linkTokenExpiration = null;
      state.linkTokenError = null;
    },

    // Set syncing state for specific account
    setSyncingAccount: (state, action) => {
      const { accountId, syncing } = action.payload;
      if (syncing) {
        state.syncingAccounts[accountId] = true;
      } else {
        delete state.syncingAccounts[accountId];
      }
    },

    // Set disconnecting state for specific account
    setDisconnectingAccount: (state, action) => {
      const { accountId, disconnecting } = action.payload;
      if (disconnecting) {
        state.disconnectingAccounts[accountId] = true;
      } else {
        delete state.disconnectingAccounts[accountId];
      }
    },

    // Reset all state
    resetPlaidState: (state) => {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      // Create link token cases
      .addCase(createLinkToken.pending, (state) => {
        state.linkTokenLoading = true;
        state.linkTokenError = null;
      })
      .addCase(createLinkToken.fulfilled, (state, action) => {
        state.linkTokenLoading = false;
        state.linkToken = action.payload.link_token;
        state.linkTokenExpiration = action.payload.expiration;
        state.linkTokenError = null;
      })
      .addCase(createLinkToken.rejected, (state, action) => {
        state.linkTokenLoading = false;
        state.linkTokenError = action.payload;
        state.linkToken = null;
        state.linkTokenExpiration = null;
      })

      // Exchange public token cases
      .addCase(exchangePublicToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exchangePublicToken.fulfilled, (state, action) => {
        state.loading = false;
        // Add new accounts to the list
        if (action.payload.accounts) {
          action.payload.accounts.forEach((newAccount) => {
            const existingIndex = state.connectedAccounts.findIndex(
              (account) => account.id === newAccount.id
            );
            if (existingIndex === -1) {
              state.connectedAccounts.push(newAccount);
            }
          });
        }
        state.error = null;
        // Clear link token after successful connection
        state.linkToken = null;
        state.linkTokenExpiration = null;
      })
      .addCase(exchangePublicToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch connected accounts cases
      .addCase(fetchConnectedAccounts.pending, (state) => {
        state.accountsLoading = true;
        state.error = null;
      })
      .addCase(fetchConnectedAccounts.fulfilled, (state, action) => {
        state.accountsLoading = false;
        state.connectedAccounts = action.payload || [];
        state.isInitialized = true;
        state.error = null;
      })
      .addCase(fetchConnectedAccounts.rejected, (state, action) => {
        state.accountsLoading = false;
        state.error = action.payload;
        state.isInitialized = true;
      })

      // Sync account transactions cases
      .addCase(syncAccountTransactions.pending, (state, action) => {
        const accountId = action.meta.arg.accountId;
        state.syncingAccounts[accountId] = true;
      })
      .addCase(syncAccountTransactions.fulfilled, (state, action) => {
        const { accountId } = action.payload;
        delete state.syncingAccounts[accountId];
        state.lastSyncTime = new Date().toISOString();

        // Update account balance if provided
        if (action.payload.account_balance !== undefined) {
          const accountIndex = state.connectedAccounts.findIndex(
            (account) => account.id === accountId
          );
          if (accountIndex !== -1) {
            state.connectedAccounts[accountIndex].current_balance =
              action.payload.account_balance;
          }
        }
      })
      .addCase(syncAccountTransactions.rejected, (state, action) => {
        const accountId = action.meta.arg.accountId;
        delete state.syncingAccounts[accountId];
        state.error = action.payload;
      })

      // Disconnect bank account cases
      .addCase(disconnectBankAccount.pending, (state, action) => {
        const accountId = action.meta.arg;
        state.disconnectingAccounts[accountId] = true;
      })
      .addCase(disconnectBankAccount.fulfilled, (state, action) => {
        const { accountId } = action.payload;
        delete state.disconnectingAccounts[accountId];
        // Remove account from connected accounts list
        state.connectedAccounts = state.connectedAccounts.filter(
          (account) => account.id !== accountId
        );
      })
      .addCase(disconnectBankAccount.rejected, (state, action) => {
        const accountId = action.meta.arg;
        delete state.disconnectingAccounts[accountId];
        state.error = action.payload;
      })

      // Refresh all accounts cases
      .addCase(refreshAllAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshAllAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.connectedAccounts = action.payload || [];
        state.lastSyncTime = new Date().toISOString();
        state.error = null;
      })
      .addCase(refreshAllAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  clearLinkToken,
  setSyncingAccount,
  setDisconnectingAccount,
  resetPlaidState,
} = plaidSlice.actions;

export default plaidSlice.reducer;
